import { ShaderMaterial, AdditiveBlending, Color, DoubleSide, Vector3 } from 'three';
import { noiseGLSL } from '../shaders/lib/noise.glsl.js';
import { sharedUniforms } from '../core/FrameUniforms.js';

/**
 * The path is not a beam: it is a quiet spatial scar with one readable pulse.
 * The pulse travels out during the cast and reverses direction after the gate
 * opens, which is the visual contract of Backflow Rift's gameplay mechanic.
 */
const RIFT_PATH_VERTEX = /* glsl */ `
  uniform vec3  uOrigin;
  uniform vec3  uTarget;
  uniform vec3  uSide;
  uniform float uWidth;
  uniform float uLift;
  uniform float uArch;

  varying float vT;
  varying float vSide;

  void main() {
    float t = position.x + 0.5;
    float side = position.y * 2.0;
    vec3 world = mix(uOrigin, uTarget, t);
    world += uSide * side * uWidth;
    world.y += uLift + uArch * sin(t * 3.14159265359);

    vT = t;
    vSide = side;
    gl_Position = projectionMatrix * viewMatrix * vec4(world, 1.0);
  }
`;

const RIFT_PATH_FRAGMENT = /* glsl */ `
  uniform float uTime;
  uniform float uTravelProgress;
  uniform float uReturnProgress;
  uniform float uPulseWidth;
  uniform float uPulseIntensity;
  uniform float uMemoryOpacity;
  uniform float uFade;
  uniform float uOpacity;
  uniform float uSeed;
  uniform vec3  uColorCore;
  uniform vec3  uColorA;
  uniform vec3  uColorB;
  uniform float uGlobalGlow;

  varying float vT;
  varying float vSide;

  ${noiseGLSL}

  void main() {
    bool returning = uReturnProgress >= 0.0;
    float pulseT = returning ? 1.0 - uReturnProgress : uTravelProgress;
    float pulse = exp(-pow(abs(vT - pulseT) / max(uPulseWidth, 0.001), 2.0));

    // A dim memory remains behind the pulse, but it never fills the corridor
    // like a beam. The return wave is therefore legible as a separate event.
    float memory = returning
      ? smoothstep(0.02, 0.35, 1.0 - abs(vT - pulseT) * 4.0)
      : step(vT, uTravelProgress) * 0.6;
    float filament = 0.5 + 0.5 * sin(
      vT * 46.0 + uTime * 3.0 + vSide * 4.0 + uSeed * 11.0
    );
    float edge = pow(max(0.0, 1.0 - abs(vSide)), 2.4);
    float grain = 0.72 + 0.28 * snoise(vec3(vT * 16.0, vSide * 3.0, uTime * 0.7 + uSeed));

    float alpha = (pulse * uPulseIntensity + memory * uMemoryOpacity * filament * 0.45);
    alpha *= edge * grain * uFade * uOpacity;
    if (alpha < 0.003) discard;

    float colorMix = clamp(0.5 + 0.5 * sin(vT * 10.0 + uTime * 0.8 + uSeed), 0.0, 1.0);
    vec3 color = mix(uColorA, uColorB, colorMix);
    color = mix(color, uColorCore, smoothstep(0.25, 1.0, pulse));
    color *= uGlobalGlow * (0.75 + pulse * 1.8);
    gl_FragColor = vec4(color, alpha);
  }
`;

const RIFT_GATE_VERTEX = /* glsl */ `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const RIFT_GATE_FRAGMENT = /* glsl */ `
  uniform float uTime;
  uniform float uSpin;
  uniform float uPulse;
  uniform float uPulseSpeed;
  uniform float uOpen;
  uniform float uFade;
  uniform float uGlow;
  uniform float uOpacity;
  uniform float uSeed;
  uniform vec3  uColorCore;
  uniform vec3  uColorGate;
  uniform vec3  uColorEcho;
  uniform float uGlobalGlow;

  varying vec2 vUv;

  ${noiseGLSL}

  void main() {
    vec2 q = (vUv - 0.5) * 2.0;
    float radius = length(q);
    if (radius > 1.0) discard;

    float angle = atan(q.y, q.x);
    float movingRadius = 0.58 + 0.06 * sin(uTime * uPulseSpeed + uSeed);
    float rim = exp(-pow(abs(radius - movingRadius) * 34.0, 2.0));
    float innerRim = exp(-pow(abs(radius - 0.36 - 0.04 * sin(angle * 4.0 - uTime * 2.0)) * 28.0, 2.0));

    float spokes = pow(max(0.0, cos(angle * 7.0 + uTime * uSpin * 6.2831)), 18.0);
    float counterSpokes = pow(max(0.0, cos(angle * 5.0 - uTime * uSpin * 3.7 + radius * 9.0)), 13.0);
    float swirl = 0.5 + 0.5 * snoise(vec3(q * 3.5 + vec2(uTime * 0.18), uSeed));
    float hollow = smoothstep(0.12, 0.38, radius);

    float alpha = (rim * 1.5 + innerRim * 0.9 + spokes * 0.65 + counterSpokes * 0.35);
    alpha += swirl * hollow * 0.16;
    alpha *= uOpen * uFade * uOpacity;
    if (alpha < 0.003) discard;

    vec3 color = mix(uColorGate, uColorEcho, clamp(radius * 1.2, 0.0, 1.0));
    color = mix(color, uColorCore, smoothstep(0.0, 1.0, rim + innerRim));
    color *= uGlow * uGlobalGlow * (1.0 + uPulse * 0.35);
    gl_FragColor = vec4(color, alpha);
  }
`;

export function createRiftPathMaterial() {
  const material = new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: AdditiveBlending,
    side: DoubleSide,
    toneMapped: false,
    uniforms: sharedUniforms({
      uOrigin: { value: new Vector3() },
      uTarget: { value: new Vector3(0, 0, 1) },
      uSide: { value: new Vector3(1, 0, 0) },
      uWidth: { value: 0.5 },
      uLift: { value: 0.05 },
      uArch: { value: 0.3 },
      uTravelProgress: { value: 0 },
      uReturnProgress: { value: -1 },
      uPulseWidth: { value: 0.08 },
      uPulseIntensity: { value: 1.4 },
      uMemoryOpacity: { value: 0.25 },
      uFade: { value: 1 },
      uOpacity: { value: 1 },
      uSeed: { value: 0 },
      uColorCore: { value: new Color(1, 1, 1) },
      uColorA: { value: new Color(0.6, 0.2, 1) },
      uColorB: { value: new Color(0.2, 0.8, 1) }
    }),
    vertexShader: RIFT_PATH_VERTEX,
    fragmentShader: RIFT_PATH_FRAGMENT
  });

  return material;
}

export function createRiftGateMaterial() {
  return new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: AdditiveBlending,
    side: DoubleSide,
    toneMapped: false,
    uniforms: sharedUniforms({
      uSpin: { value: 0.4 },
      uPulse: { value: 0 },
      uPulseSpeed: { value: 3 },
      uOpen: { value: 0 },
      uFade: { value: 1 },
      uGlow: { value: 1.8 },
      uOpacity: { value: 1 },
      uSeed: { value: 0 },
      uColorCore: { value: new Color(1, 1, 1) },
      uColorGate: { value: new Color(0.6, 0.25, 1) },
      uColorEcho: { value: new Color(0.3, 0.9, 1) }
    }),
    vertexShader: RIFT_GATE_VERTEX,
    fragmentShader: RIFT_GATE_FRAGMENT
  });
}
